import { useCallback, useEffect, useState } from "react";
import { getClientContactsApi } from "@/services/api-service/customers/customers-api";
import { useGomakeAxios } from "@/hooks";
import {
  createInitialSendToCustomerContactState,
  type ISendToCustomerContactState,
} from "./types";

interface IUseSendToCustomerContactOptions {
  open: boolean;
  clientId?: string | null;
  defaultContactName?: string;
  defaultContactPhone?: string;
  defaultContactMail?: string;
}

const mapContact = (contact: any) => ({
  ...contact,
  id: contact.id || contact.contactID || contact.Id || contact.ContactID,
  contactName: contact.contactName || contact.name || "",
  contactMail: contact.contactMail || contact.mail || contact.email || "",
  contactPhone: contact.contactPhone || contact.phone || "",
});

const useSendToCustomerContact = ({
  open,
  clientId,
  defaultContactName = "",
  defaultContactPhone = "",
  defaultContactMail = "",
}: IUseSendToCustomerContactOptions) => {
  const { callApi } = useGomakeAxios();
  const [state, setState] = useState<ISendToCustomerContactState>(
    createInitialSendToCustomerContactState()
  );
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const resetState = useCallback(() => {
    setState(
      createInitialSendToCustomerContactState({
        contactName: defaultContactName,
        contactPhone: defaultContactPhone,
        contactMail: defaultContactMail,
      })
    );
  }, [defaultContactMail, defaultContactName, defaultContactPhone]);

  const applyContact = useCallback((contact: any, index: number) => {
    const mapped = mapContact(contact);
    setState((prev) => ({
      ...prev,
      selectedContactIndex: index,
      contactName: mapped.contactName,
      contactPhone: mapped.contactPhone,
      contactMail: mapped.contactMail,
    }));
  }, []);

  const loadContacts = useCallback(async () => {
    if (!clientId) {
      resetState();
      return;
    }

    setIsLoadingContacts(true);
    const callBack = (res: any) => {
      setIsLoadingContacts(false);
      if (res?.success && Array.isArray(res?.data)) {
        const contacts = res.data.map(mapContact);
        let defaultIndex = 0;

        if (defaultContactMail || defaultContactName) {
          const matchingIndex = contacts.findIndex((contact) => {
            const contactMail = (contact.contactMail || "").trim().toLowerCase();
            const contactName = (contact.contactName || "").trim().toLowerCase();
            return (
              (defaultContactMail &&
                contactMail === defaultContactMail.trim().toLowerCase()) ||
              (defaultContactName &&
                contactName === defaultContactName.trim().toLowerCase())
            );
          });

          if (matchingIndex >= 0) {
            defaultIndex = matchingIndex;
          }
        }

        const selectedContact = contacts[defaultIndex];
        setState((prev) => ({
          ...prev,
          documentContacts: contacts,
          selectedContactIndex: contacts.length > 0 ? defaultIndex : -1,
          contactName:
            selectedContact?.contactName ||
            defaultContactName ||
            prev.contactName,
          contactPhone:
            selectedContact?.contactPhone ||
            defaultContactPhone ||
            prev.contactPhone,
          contactMail:
            selectedContact?.contactMail ||
            defaultContactMail ||
            prev.contactMail,
        }));
        return;
      }

      setState(
        createInitialSendToCustomerContactState({
          contactName: defaultContactName,
          contactPhone: defaultContactPhone,
          contactMail: defaultContactMail,
        })
      );
    };

    await getClientContactsApi(callApi, callBack, { ClientId: clientId });
  }, [
    callApi,
    clientId,
    defaultContactMail,
    defaultContactName,
    defaultContactPhone,
    resetState,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadContacts();
  }, [open, loadContacts]);

  const onChangeField = useCallback((key: string, value: string | boolean) => {
    setState((prev) => {
      if (key === "documentContacts") {
        if (!value) {
          return {
            ...prev,
            selectedContactIndex: -1,
            contactName: "",
            contactPhone: "",
            contactMail: "",
          };
        }

        const selectedIndex = prev.documentContacts.findIndex(
          (contact) => contact.id === value
        );

        if (selectedIndex < 0) {
          return prev;
        }

        const selectedContact = mapContact(prev.documentContacts[selectedIndex]);
        return {
          ...prev,
          selectedContactIndex: selectedIndex,
          contactName: selectedContact.contactName,
          contactPhone: selectedContact.contactPhone,
          contactMail: selectedContact.contactMail,
        };
      }

      if (
        key === "contactName" ||
        key === "contactPhone" ||
        key === "contactMail"
      ) {
        const selectedIndex = prev.selectedContactIndex;
        if (
          selectedIndex >= 0 &&
          selectedIndex < prev.documentContacts.length
        ) {
          return {
            ...prev,
            [key]: value,
            documentContacts: prev.documentContacts.map((contact, index) =>
              index === selectedIndex
                ? { ...contact, [key]: value }
                : contact
            ),
          };
        }
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  const onNotificationChange = useCallback(
    (key: "sendBySms" | "sendByMail" | "sendByWhatsapp", value: boolean) => {
      setState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  return {
    state,
    isLoadingContacts,
    onChangeField,
    onNotificationChange,
    applyContact,
    resetState,
  };
};

export { useSendToCustomerContact };
